<template lang="pug">
.artifact-card(:class="cardModifierClass")
    .artifact-card__head
        i.artifact-card__icon.fas(v-if="icon" :class="icon")
        h4.artifact-card__title {{ title }}
        nobr.artifact-card__date(v-if="sentAt") {{ sentAt | timestamp2date(dateTimeFormat) }}
        span.artifact-card__status(v-else-if="warning")
            i.el-icon-warning
            | {{ warning }}
        //- Header-level action slot, e.g. a card-wide "View full list PDF"
        //- button that belongs to the card itself, not to any inner item.
        .artifact-card__header-action(v-if="$slots['header-action']")
            slot(name="header-action")

    .artifact-card__body(v-if="$slots.default")
        slot

    EngagementStrip(
        v-if="sentEmail || (sentAt && trackable)"
        :sent-email="sentEmail"
        :fallback-sent-at="sentAt"
        :date-time-format="dateTimeFormat"
    )

    .artifact-card__actions(v-if="$slots.actions")
        slot(name="actions")
</template>

<script>
import { timestamp2date } from '@/filters';
import EngagementStrip from '@/extensions/shop/views/Purchase/Form/Partials/EngagementStrip.vue';

export default {
    name: 'ArtifactCard',
    components: { EngagementStrip },
    filters: { timestamp2date },
    props: {
        title: {
            type: String,
            required: true,
        },
        icon: {
            type: String,
            default: '',
        },
        // Unix timestamp when the artefact was sent / created. When set
        // alongside a SentEmail row, the strip lights up; alone, the row
        // shows a "tracking unavailable" hint.
        sentAt: {
            type: [Number, String],
            default: null,
        },
        // Single SentEmail row corresponding to this artefact, populated
        // by the parent via the X-Type matcher.
        sentEmail: {
            type: Object,
            default: null,
        },
        // Whether this artefact is mail-trackable at all (PDFs aren't).
        trackable: {
            type: Boolean,
            default: true,
        },
        // Optional warning string ("Receipt has not been generated yet")
        // rendered when sentAt is absent.
        warning: {
            type: String,
            default: '',
        },
        dateTimeFormat: {
            type: String,
            required: true,
        },
        // Visual variant — 'default' | 'warning' | 'success'.
        variant: {
            type: String,
            default: 'default',
        },
    },
    computed: {
        cardModifierClass() {
            return `artifact-card--${this.variant}`;
        },
    },
};
</script>

<style lang="sass" scoped>
.artifact-card
    background: var(--background-base, #fafafa)
    border: 1px solid var(--border-color, #ebeef5)
    border-radius: 6px
    padding: 12px 16px

.artifact-card--warning
    background: rgba(255, 152, 0, 0.06)
    border-color: rgba(255, 152, 0, 0.4)

.artifact-card--success
    background: rgba(76, 175, 80, 0.04)
    border-color: rgba(76, 175, 80, 0.3)

.artifact-card__head
    display: flex
    align-items: center
    gap: 8px
    padding-bottom: 8px
    margin-bottom: 8px
    border-bottom: 1px solid var(--border-color, #ebeef5)

.artifact-card__icon
    color: var(--color-text-secondary, #606266)
    font-size: 14px

.artifact-card__title
    margin: 0
    font-size: 14px
    font-weight: 600
    flex: 1

.artifact-card__date
    color: var(--color-text-secondary, #606266)
    font-size: 12px
    white-space: nowrap

.artifact-card__status
    color: $--color-warning
    font-size: 12px
    display: flex
    align-items: center
    gap: 4px

.artifact-card__header-action
    margin-left: auto
    display: flex
    align-items: center

.artifact-card__body
    font-size: 13px
    color: var(--color-text-regular, #303133)
    padding: 4px 0
    // When the body is the last element (cards that render their actions
    // inside the default slot, e.g. Cancellation / Shipping confirmations),
    // drop the bottom padding so the gap below the content matches the
    // 12px card padding of cards whose last child is __actions.
    &:last-child
        padding-bottom: 0

.artifact-card__actions
    display: flex
    flex-wrap: wrap
    gap: 8px
    padding-top: 6px
    margin-top: 6px
    // No border-top here — the head already carries a border-bottom
    // that separates the title from everything below. Adding a second
    // line above the actions stacked visually with the head's line on
    // cards that have no body content (Rechnung, Lieferschein).
    a
        font-size: 12px
</style>
